import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { rememberServerProfile } from '../services/profileService';
import { gatewayFetch } from '../services/gateway';

// Migrated 2026-06-07 from Firebase Callable function `update_profile_callable`
// to the unified API gateway. All API calls now go through one entry point;
// the previous direct cloudfunctions.net URL is gone. Auth is now an explicit
// `Authorization: Bearer <id-token>` header (Callable used to handle it for us).

// Types match the backend `api/user_api.py` _handle_update_profile contract.
interface UpdateProfileRequest {
  name?: string;
  company?: string;
  role?: string;
  interests?: string[];
  onboardingComplete?: boolean;
  // `avatarUrl` was here and no caller ever passed it. It is not in the
  // server's `_ALLOWED_PROFILE_FIELDS`, so a caller that started passing it
  // would have been dropped in silence — avatars go through the dedicated
  // `update-avatar` action instead. Found by `profileFieldParity.test.ts` on
  // its first run; removed rather than added server-side, because opening a
  // write path nothing needs is the wrong direction.
  hasInitiatedProfile?: boolean;
  /** Must also appear in `_ALLOWED_PROFILE_FIELDS` (functions/api/user_api.py)
   *  or the write is dropped server-side without an error. */
  onboardingDismissed?: boolean;
  quizPromptDismissed?: boolean;
  /** Per-signal opt-outs (S.1.2): the FULL map each time — the server stores
   *  it as sent (after dropping unknown ids), so a partial map would erase
   *  every other toggle. */
  signalPreferences?: Record<string, boolean>;
  onboardingTasks?: {
    quizCompleted?: boolean;
    firstSearch?: boolean;
    firstWatchlist?: boolean;
    firstPortfolio?: boolean;
    firstChat?: boolean;
    starsEarned?: number;
    lastTaskCompletedAt?: Date;
  };
  preferences?: {
    emailNotifications?: boolean;
    dailyDigest?: boolean;
    marketAlerts?: boolean;
    reportFrequency?: 'daily' | 'weekly' | 'realtime';
    theme?: 'light' | 'dark' | 'auto';
    [key: string]: any;
  };
}

interface UpdateProfileResponse {
  success: boolean;
  message: string;
  updatedFields: string[];
}

interface UseProfileUpdateReturn {
  updating: boolean;
  error: string | null;
  updateProfile: (profileData: UpdateProfileRequest) => Promise<boolean>;
  clearError: () => void;
}


export const useProfileUpdate = (): UseProfileUpdateReturn => {
  const { user } = useAuth();
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const updateProfile = async (profileData: UpdateProfileRequest): Promise<boolean> => {
    if (!user) {
      setError('You must be logged in to update your profile');
      return false;
    }

    setUpdating(true);
    setError(null);

    try {
      const response = await gatewayFetch('user', 'update-profile', { auth: 'required', body: profileData });

      // The backend returns a typed error_code on 4xx; surface a friendly
      // message based on that instead of dumping the raw status to the user.
      if (!response.ok) {
        let errorCode = String(response.status);
        let errorMessage = `Failed to update profile (${response.status})`;
        try {
          const errBody = await response.json();
          errorCode = errBody.error_code || errorCode;
          errorMessage = errBody.error || errorMessage;
        } catch {
          // body wasn't JSON — keep defaults
        }
        switch (errorCode) {
          case 'UNAUTHENTICATED':
            setError('You must be logged in to update your profile');
            break;
          case 'VALIDATION_FAILED':
            setError(errorMessage);
            break;
          case 'METHOD_NOT_ALLOWED':
            setError('Profile update endpoint misconfigured. Please contact support.');
            break;
          default:
            setError(errorMessage);
        }
        return false;
      }

      const data: UpdateProfileResponse = await response.json();
      if (data.success) {
        // Remember what we just wrote ([[BUGS #190]]). `AuthContext` layers the
        // server-owned fields over every Firestore snapshot; without this the
        // cache still holds the value from sign-in, so the next snapshot would
        // put the OLD one back over the change just made — the same revert,
        // one step further along.
        rememberServerProfile(profileData as Record<string, never>);
        return true;
      }
      setError(data.message || 'Failed to update profile');
      return false;
    } catch (err: any) {
      console.error('Error updating profile:', err);
      setError('Failed to update profile. Please try again.');
      return false;
    } finally {
      setUpdating(false);
    }
  };

  const clearError = () => setError(null);

  return {
    updating,
    error,
    updateProfile,
    clearError,
  };
};
